import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, Upload, ShieldAlert, Trash2, Plus, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Settings = () => {
  const { getAuthHeader, user, register } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [users, setUsers] = useState([]);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'senior_accountant' });

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[400px] text-muted-foreground">
        <ShieldAlert className="w-12 h-12 mb-4" />
        <p className="text-xl font-medium">Access Denied</p>
        <p className="text-sm mt-1">This page is only accessible to admins.</p>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`, { headers: getAuthHeader() });
      setUsers(res.data);
    } catch {
      toast.error('Failed to fetch users');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const result = await register(userForm.name, userForm.email || null, userForm.password, userForm.role);
    if (result.success) {
      toast.success('User created successfully');
      setIsUserOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'senior_accountant' });
      fetchUsers();
    } else {
      toast.error(result.error);
    }
  };

  const handleBackup = async () => {
    setIsBackingUp(true);
    try {
      const response = await axios.post(`${API}/admin/backup`, {}, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const today = new Date().toISOString().slice(0, 10);
      const filename = `cladakis_backup_${today}.json`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch {
      toast.error('Failed to create backup');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a .json backup file');
      return;
    }
    setSelectedFile(file);
    setIsConfirmOpen(true);
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    setIsRestoring(true);
    setIsConfirmOpen(false);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      await axios.post(`${API}/admin/restore`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Data restored successfully. Please refresh the page.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleResetData = async () => {
    setIsResetting(true);
    try {
      await axios.post(`${API}/admin/reset-data`, {}, { headers: getAuthHeader() });
      toast.success('All business data has been cleared successfully!');
      setIsResetDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="text-4xl font-bold tracking-tight mb-8">Settings</h1>

      <Tabs defaultValue="backup" onValueChange={(v) => { if (v === 'users') fetchUsers(); }}>
        <TabsList className="mb-6">
          <TabsTrigger value="backup">Backup</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="reset">Reset</TabsTrigger>
        </TabsList>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-6">
          {/* Backup Section */}
          <Card className="p-6 border border-border">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Download className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-1">Backup Data</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Download a complete snapshot of all your ERP data (orders, invoices, employees, finance, outsourcing, and more) as a single JSON file. Store it somewhere safe — like OneDrive.
                </p>
                <Button onClick={handleBackup} disabled={isBackingUp}>
                  <Download className="w-4 h-4 mr-2" />
                  {isBackingUp ? 'Creating backup...' : 'Download Backup Now'}
                </Button>
              </div>
            </div>
          </Card>

          {/* Restore Section */}
          <Card className="p-6 border border-destructive/40">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-destructive/10 rounded-lg">
                <Upload className="w-6 h-6 text-destructive" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-1 text-destructive">Restore from Backup</h2>
                <p className="text-sm text-muted-foreground mb-1">
                  Upload a previously downloaded backup file to restore all data.
                </p>
                <p className="text-sm font-medium text-destructive mb-4">
                  Warning: This will permanently replace ALL current data with the contents of the backup file.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isRestoring}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isRestoring ? 'Restoring...' : 'Select Backup File to Restore'}
                </Button>
              </div>
            </div>
          </Card>

          {/* Task Scheduler Info */}
          <Card className="p-6 border border-border bg-muted/20">
            <h2 className="text-base font-semibold mb-2">Automated Daily Backups</h2>
            <p className="text-sm text-muted-foreground mb-3">
              To set up automatic daily backups to your OneDrive folder, use the included script:
            </p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Open <strong>backup.bat</strong> in the <code>backend/</code> folder and set your OneDrive path</li>
              <li>Open <strong>Windows Task Scheduler</strong> → Create Basic Task</li>
              <li>Set trigger: Daily at a time when the PC is on (e.g. 11:00 PM)</li>
              <li>Set action: Start a program → select <code>backup.bat</code></li>
              <li>Enable "Run whether user is logged on or not"</li>
            </ol>
            <p className="text-sm text-muted-foreground mt-3">
              Backups older than 90 days are automatically deleted. Backup progress is logged to <code>backend/backup.log</code>.
            </p>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="p-6 border border-border">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold">User Accounts</h2>
              </div>
              <Dialog open={isUserOpen} onOpenChange={setIsUserOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent hover:bg-accent/90">
                    <Plus className="w-4 h-4" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Name <span className="text-destructive">*</span></label>
                      <input
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                        value={userForm.name}
                        onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        required
                        placeholder="Full name (used to login)"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Password <span className="text-destructive">*</span></label>
                      <input
                        type="password"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Role</label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                        value={userForm.role}
                        onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      >
                        <option value="admin">Admin (Full Access)</option>
                        <option value="senior_accountant">Senior Accountant</option>
                        <option value="viewer">Viewer (Read Only)</option>
                        <option value="order_processor">Order Processor</option>
                        <option value="delivery_coordinator">Delivery Coordinator</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email <span className="text-muted-foreground text-xs">(optional)</span></label>
                      <input
                        type="email"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90">Create User</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-3 text-start font-semibold">Name</th>
                    <th className="border border-border p-3 text-start font-semibold">Role</th>
                    <th className="border border-border p-3 text-start font-semibold">Email</th>
                    <th className="border border-border p-3 text-start font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="border border-border p-3 font-medium">{u.name}</td>
                      <td className="border border-border p-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted border border-border capitalize">
                          {u.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="border border-border p-3 text-muted-foreground">{u.email || '—'}</td>
                      <td className="border border-border p-3 text-muted-foreground">{u.created_at ? u.created_at.slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={4} className="border border-border p-6 text-center text-muted-foreground">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Reset Tab */}
        <TabsContent value="reset">
          <Card className="p-6 border border-destructive/50 bg-destructive/5">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-destructive">{t('dangerZone')}</h3>
                <p className="text-sm text-muted-foreground">{t('resetDataWarning')}</p>
              </div>
              <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" className="gap-2 ms-4 shrink-0">
                    <Trash2 className="w-4 h-4" />
                    <span>{t('resetAllData')}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-destructive">{t('resetDataTitle')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-destructive/10 rounded-md border border-destructive/50">
                      <p className="text-sm font-semibold text-destructive mb-2">
                        ⚠️ {t('resetDataConfirmTitle')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('resetDataConfirmText')}
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                        <li>{t('resetDataList1')}</li>
                        <li>{t('resetDataList2')}</li>
                        <li>{t('resetDataList3')}</li>
                        <li>{t('resetDataList4')}</li>
                        <li>{t('resetDataList5')}</li>
                        <li>{t('resetDataList6')}</li>
                        <li>{t('resetDataList7')}</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-3 font-semibold">
                        {t('userAccountsPreserved')}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setIsResetDialogOpen(false)} disabled={isResetting}>
                        {t('cancel')}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleResetData}
                        disabled={isResetting}
                      >
                        {isResetting ? t('resetting') : t('yesDeleteAllData')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-destructive">Confirm Restore</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm">
              You are about to restore from:
            </p>
            <p className="text-sm font-medium bg-muted/40 p-2 rounded border border-border font-mono break-all">
              {selectedFile?.name}
            </p>
            <p className="text-sm font-medium text-destructive">
              This will permanently wipe all current data and replace it with the backup. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setIsConfirmOpen(false); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                Cancel
              </Button>
              <Button
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                onClick={handleRestore}
              >
                Yes, Restore
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
